import React, { useCallback, useEffect, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { closeDashboardDialog } from '@actions/dashboard-actions';
import { setDatatableFilter } from '@actions/data-table-actions';
import { getEntityTypeLabel } from '@constants/config';
import * as dialog from '@constants/dialogs';
import { getFilters } from '@selectors/data-table-selector';
import BaseDateRangeDialog, { useDateRangeDialog } from '@shared/dialogs/base-date-range-dialog';
import { getDataType, getSubType } from '@utils/dashboard-utils';
import { decodeDateFilters, generateDateFilters, generateSingleDateFilters } from '@utils/date-utils';

/**
 * Dashboard date range dialog component.
 * @param {object} props - Component props.
 * @returns {JSX.Element|null} The rendered dialog or null.
 */
const DashboardDateRangeDialog = ({ dataType: dataTypeProp, location, params }) => {
  const dispatch = useDispatch();

  const computedDataType = getDataType(params, location);
  const dataType = dataTypeProp || computedDataType;
  const subType = getSubType(dataType, { params, location });
  const activeDialogs = useSelector(state => state.dashboard.activeDialogs);
  const dialogParams = useSelector(state => state.dashboard.dialogParams[dialog.DATE_RANGE]);
  const currentFilters = useSelector(state => getFilters(state, { dataType, subType }));

  const { start: startProp, end: endProp, contain: containProp } = decodeDateFilters(currentFilters);

  const {
    start,
    end,
    contain,
    resetToDefault,
    handleStartChange,
    handleEndChange,
    toggleContain
  } = useDateRangeDialog({
    start: startProp,
    end: endProp,
    contain: containProp,
    useGlobalDefaults: false
  });

  // Sync state when dialog opens
  const prevOpenRef = useRef(activeDialogs[dialog.DATE_RANGE]);
  useEffect(() => {
    const isOpen = activeDialogs[dialog.DATE_RANGE];
    const wasOpen = prevOpenRef.current;
    if (!wasOpen && isOpen) {
      resetToDefault();
    }
    prevOpenRef.current = isOpen;
  }, [activeDialogs, resetToDefault]);

  const isMultiple = dataType !== 'batch';
  const showContain = dataType !== 'batch';
  const dataTypeDisplayName = getEntityTypeLabel(dataType);

  const getFiltersValue = useCallback(() => {
    if (isMultiple) {
      return generateDateFilters({ start, end, contain });
    }
    const fieldName = dialogParams?.fieldName || 'created';
    return generateSingleDateFilters({ start, end, contain }, fieldName);
  }, [isMultiple, start, end, contain, dialogParams]);

  const closeDialog = useCallback(() => {
    dispatch(closeDashboardDialog(dialog.DATE_RANGE));
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    resetToDefault();
    closeDialog();
  }, [resetToDefault, closeDialog]);

  const handleApply = useCallback(() => {
    dispatch(setDatatableFilter(dataType, subType, getFiltersValue()));
    closeDialog();
  }, [dispatch, dataType, subType, getFiltersValue, closeDialog]);

  return (
    <BaseDateRangeDialog
      isOpen={activeDialogs[dialog.DATE_RANGE]}
      start={start}
      end={end}
      contain={contain}
      handleStartChange={handleStartChange}
      handleEndChange={handleEndChange}
      toggleContain={toggleContain}
      onCancel={handleCancel}
      onApply={handleApply}
      dataTypeDisplayName={dataTypeDisplayName}
      showContain={showContain}
    />
  );
};

DashboardDateRangeDialog.propTypes = {
  dataType: PropTypes.string,
  location: PropTypes.object,
  params: PropTypes.object
};

export default memo(DashboardDateRangeDialog);
